import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import productStatusReference from "../common/dictionaries/productsStatus.json";
import { IoIosCheckmarkCircle } from "react-icons/io";
import { PiWarningFill } from "react-icons/pi";
import { RiRepeat2Line } from "react-icons/ri";

interface ProductVerificationTooltipProps {
  productId: string;
  productStatus: number;
  productQuantityStatus: number;
  productVariantStatus: number;
  productConfidenceSource: string;
  hasVariants: boolean;
  productIdCorrected?: boolean;
  quantityIdCorrected?: boolean;
  variantIdCorrected?: boolean;
  packingCustomerIdCorrected?: boolean;
  hasPackingCustomer?: boolean;
}

interface CheckItemProps {
  label: string;
  status: string;
  isValid: boolean;
}

interface Product {
  product_id: string;
  product_id_substitution: string;
  product_availability_description: string;
}

const CheckItem: React.FC<CheckItemProps> = ({ label, status, isValid }) => (
  <div className="flex items-start gap-2 py-1">
    {isValid ? (
      <IoIosCheckmarkCircle className="text-vistagreen text-lg mt-0.5 flex-shrink-0" />
    ) : (
      <PiWarningFill className="text-orange-500 text-lg mt-0.5 flex-shrink-0" />
    )}
    <div className="flex flex-col">
      <span className="text-sm font-medium text-gray-700">{label}</span>
      <span className={`text-xs ${isValid ? "text-vistagreen" : "text-orange-500"}`}>
        {status}
      </span>
    </div>
  </div>
);

const ProductVerificationTooltip: React.FC<ProductVerificationTooltipProps> = ({
  productId,
  productStatus,
  productQuantityStatus,
  productVariantStatus,
  productConfidenceSource,
  hasVariants,
  productIdCorrected,
  quantityIdCorrected,
  variantIdCorrected,
  packingCustomerIdCorrected,
  hasPackingCustomer,
}) => {
  const { t } = useTranslation();

  const { data: productsData } = useQuery<Product[]>({
    queryKey: ["allProducts"],
    enabled: false,
  });
  
  const currentProduct = useMemo(() => {
    if (!productsData || !productId) return null;
    return productsData.find((p) => p.product_id === productId);
  }, [productsData, productId]);

  const isSelectionValid = productIdCorrected ?? productStatus >= 75;
  const isQuantityValid = quantityIdCorrected ?? productQuantityStatus >= 75;
  const isVariantValid = !hasVariants || (variantIdCorrected ?? productVariantStatus >= 75);
  const isPackingCustomerValid = !hasPackingCustomer || (packingCustomerIdCorrected ?? true);

  const isSubstitution = productConfidenceSource === "product_substitution";
  const allValid = isSelectionValid && isQuantityValid && isVariantValid && isPackingCustomerValid && !isSubstitution;

  const getSelectionStatus = () => {
    if (productIdCorrected) {
      return t(productStatusReference["ok"]);
    }
    return productStatus < 75
      ? t(productStatusReference[productConfidenceSource as keyof typeof productStatusReference] || "product_not_found")
      : t(productStatusReference["ok"]);
  };

  const getQuantityStatus = () => {
    return isQuantityValid ? t("ok") : t("validation_required");
  };

  const getVariantStatus = () => {
    if (!hasVariants) {
      return t("product_variant_not_applicable");
    }
    return isVariantValid ? t("ok") : t("validation_required");
  };

  const getPackingCustomerStatus = () => {
    return isPackingCustomerValid ? t("ok") : t("validation_required");
  };

  return (
    <div className="p-3 min-w-[200px]">
      <div className="flex items-center gap-2 mb-3 pb-2 border-b border-gray-200">
        <div
          className={`w-2.5 h-2.5 rounded-full ${
            allValid ? "bg-vistagreen" : "bg-orange-500"
          }`}
        />
        <span className="font-semibold text-gray-800">
          {t("product_verification")}
        </span>
      </div>

      <div className="flex flex-col gap-1">
        <CheckItem
          label={t("product_selection")}
          status={getSelectionStatus()}
          isValid={isSelectionValid}
        />
        <CheckItem
          label={t("quantity")}
          status={getQuantityStatus()}
          isValid={isQuantityValid}
        />
        {hasVariants && (
          <CheckItem
            label={t("variant")}
            status={getVariantStatus()}
            isValid={isVariantValid}
          />
        )}
        {hasPackingCustomer && (
          <CheckItem
            label={t("packing_customer")}
            status={getPackingCustomerStatus()}
            isValid={isPackingCustomerValid}
          />
        )}
      </div>

      {isSubstitution && productId && (
        <div className="mt-3 pt-2 border-t border-gray-200 border-dashed">
          <div className="flex items-center gap-2">
            <RiRepeat2Line className="fill-violet-500 text-violet-500 text-2xl flex-shrink-0" />
            <span className="text-xs text-gray-500">
              {t("product_substitution_proposed")}
            </span>
          </div>
        </div>
      )}

      {currentProduct?.product_availability_description && (
        <div className="mt-3 pt-2 border-t border-gray-200 border-dashed">
          <div className="flex items-center gap-2">
            <RiRepeat2Line className="text-vistagreen text-lg flex-shrink-0" />
            <div className="flex flex-col">
              <span className="text-xs text-gray-500">
                {t("availability_info")}
              </span>
              {currentProduct?.product_availability_description && (
                <span className="text-sm text-vistagreen font-medium">
                  {currentProduct.product_availability_description}
                </span>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductVerificationTooltip;
